import axios, {AxiosError, AxiosInstance, AxiosRequestConfig} from 'axios'
import {TApiResponse} from '../types/api'
import {getErrorToast} from '../constructor/helpers/toasts'
import {DOMAIN_API_MAP, MEOWBOX_SUBDOMAIN_MAP} from './domainMap'
import {getAccessToken, setAccessToken} from '../modules/auth/utils/tokenStorage'
import {getFingerprintId} from '../modules/auth/utils/fingerprint'
import {
    getAuthMode,
    setAuthMode,
    useTokens,
    withCredentialsForApiRequests,
    withCredentialsForAuthEndpoints,
} from '../modules/auth/utils/authTransport'
import {initPaymentsFinalize} from './paymentsFinalize'

let dynamicApiUrl: string | undefined = undefined
let isRefreshingAccess = false
let refreshSubscribers: Array<(token?: string) => void> = []
let onProfileUpdated: null | (() => Promise<void> | void) = null
export {onProfileUpdated}
export const registerProfileUpdateHandler = (cb: () => Promise<void> | void) => {
    onProfileUpdated = cb
}

let currentUserId: number | null = null
export const setApiCurrentUserId = (id: number | null) => {
    currentUserId = id
}

function onAuthRoute() {
    try {
        return window.location.pathname.startsWith('/auth')
    } catch {
        return false
    }
}

const getDomainAdmin = (): string => window.location.hostname

/**
 * Устаревшая логика определения домена
 */
function getDeprecatedApiUrl(hostname: string): string {
    const parts = hostname.split('.')
    const domain = parts.slice(-2).join('.')
    const sub = parts.length > 2 ? parts[0] : null
    const strip = (u: string) => u.replace(/\/+$/, '')

    // 1) Точный ключ целого хоста
    if (DOMAIN_API_MAP[hostname]) return strip(DOMAIN_API_MAP[hostname])

    // 2) Спец-кейсы
    if (hostname === 'community.docacrm.ru') return 'https://community-api.docacrm.ru'
    if (hostname === 'yazdorov.docacrm.ru') return 'https://yazdorov-api.docacrm.ru'
    if (hostname === 'oxcrm.meowbox.ru') return 'https://crm-api.meowbox.ru'

    if (domain === 'meowbox.ru' && sub) {
        return `https://${sub}-api.meowbox.ru`
    }

    if (domain === 'meowbox.ru' && sub && MEOWBOX_SUBDOMAIN_MAP[sub]) {
        return strip(MEOWBOX_SUBDOMAIN_MAP[sub])
    }

    if (domain === 'docacrm.ru' && sub) {
        return `https://${sub}.docacrm.ru`
    }

    if (DOMAIN_API_MAP[domain]) return strip(DOMAIN_API_MAP[domain])

    // 7) Дефолт
    return `https://api.${domain}`
}

/**
 * Определение домена по запросу https://crm-api.meowbox.ru
 */
const resolveDomainApi = async (): Promise<string> => {
    try {
        const payload = {
            object: 'projects',
            command: 'get',
            data: {domain_admin: getDomainAdmin()},
        }

        const {data: response} = await axios.post<TApiResponse<Array<{domain_api: string}>>>(
            'https://crm-api.meowbox.ru',
            payload,
            {headers: {'Content-Type': 'application/json'}}
        )

        if (response?.status !== 200) {
            return getDeprecatedApiUrl(getDomainAdmin())
        }

        const records = response?.data
        if (!Array.isArray(records) || !records.length || !records[0]?.domain_api) {
            return getDeprecatedApiUrl(getDomainAdmin())
        }

        const raw = records[0].domain_api
        const hasProto = /^https?:\/\//.test(raw)
        return (hasProto ? raw : `https://${raw}`).replace(/\/+$/, '')
    } catch (e) {
        console.error('resolveDomainApi failed, fallback to deprecated:', e)
        return getDeprecatedApiUrl(getDomainAdmin())
    }
}

export const getApiUrlSync = (): string => {
    if (!dynamicApiUrl) throw new Error('API URL ещё не инициализирован')
    return dynamicApiUrl
}

export const getApiUrl = async (): Promise<string> => {
    if (dynamicApiUrl) return dynamicApiUrl
    try {
        dynamicApiUrl = await resolveDomainApi()
    } catch (e) {
        console.error('Ошибка при resolveDomainApi:', e)
    }
    return dynamicApiUrl!
}

export const initApiUrl = async () => {
    if (!dynamicApiUrl) {
        dynamicApiUrl = await getApiUrl()
    }
}

const instanse: AxiosInstance = axios.create({
    headers: {'Content-Type': 'application/json'},
    withCredentials: false,
})

instanse.interceptors.request.use(async (config) => {
    const base = await getApiUrl()
    if (!base) return Promise.reject(new Error('API URL не определён'))
    config.baseURL = base
    if (!config.headers) config.headers = {}

    config.withCredentials = withCredentialsForApiRequests()

    if (!useTokens()) {
        const t = getAccessToken()
        if (t && getAuthMode() !== 'token') setAuthMode('token')
    }

    if (useTokens()) {
        const token = getAccessToken()
        if (token) config.headers['Authorization'] = `Bearer ${token}`
    } else {
        delete (config.headers as any)['Authorization']
    }

    return config
})

instanse.interceptors.response.use(
    async (response) => {
        const original = response.config as AxiosRequestConfig & {_retry?: boolean}
        const resp = response.data as TApiResponse<any>

        if (!resp || typeof resp.status !== 'number') return response

        if (resp.status === 301 || resp.status === 302) {
            const err = new Error(
                typeof resp.data === 'string' ? resp.data : 'Требуется повторная авторизация'
            )
            ;(err as any).__APP_NEED_RELOGIN__ = true
            throw err
        }

        if (resp.status === 403 && Array.isArray(resp.data)) {
            throw new Error(resp.data[0] || 'Недостаточно прав')
        }

        if ((resp.status === 401 || resp.status === 403) && !onAuthRoute()) {
            if (!original._retry) {
                original._retry = true
                const newToken = await refreshAccessToken()
                if (newToken) {
                    return instanse(original)
                }
            }
            const err = new Error(typeof resp.data === 'string' ? resp.data : 'Не авторизован')
            throw err
        }

        if (resp.status !== 200) {
            const err = new Error(
                typeof resp.data === 'string' ? resp.data : `Ошибка (${resp.status})`
            )
            ;(err as any).__APP_STATUS__ = resp.status
            throw err
        }

        return response
    },
    async (error: AxiosError) => {
        const original = error.config as AxiosRequestConfig & {_retry?: boolean}
        const status = error.response?.status
        const serverMsg = (error.response?.data as any)?.data

        const isAuthError =
            status === 401 ||
            (status === 403 &&
                typeof serverMsg === 'string' &&
                /(некорректные.+авториза|устарел|expired|просрочен)/i.test(serverMsg))

        if (!original?._retry && isAuthError) {
            if (onAuthRoute()) return Promise.reject(error)
            original._retry = true
            const newToken = await refreshAccessToken()
            if (newToken) return instanse(original)
        }
        return Promise.reject(error)
    }
)

const resolveRefreshSubscribers = (token?: string) => {
    refreshSubscribers.forEach((cb) => cb(token))
    refreshSubscribers = []
}

async function refreshAccessToken(): Promise<string | undefined> {
    if (isRefreshingAccess) {
        return new Promise<string | undefined>((resolve) => {
            refreshSubscribers.push(resolve)
        })
    }

    isRefreshingAccess = true

    try {
        const baseUrl = await getApiUrl()
        const fingerprint =
            (window as any).__APP_FP__ ?? (await getFingerprintId().catch(() => 'fp-error'))

        const {data: response} = await axios.post<
            TApiResponse<{token: string; expires_at: number}>
        >(
            `${baseUrl}?q=2fa__tokens`,
            {
                object: '2fa',
                command: 'tokens',
                data: {type: 'access_token', finger_print: fingerprint},
            },
            {
                headers: {'Content-Type': 'application/json'},
                withCredentials: withCredentialsForAuthEndpoints(),
            }
        )

        if (response.status === 200 && response.data && (response as any).data.token) {
            const {token, expires_at} = response.data
            setAccessToken(token, expires_at)
            resolveRefreshSubscribers(token)
            return token
        }

        if (response.status === 302) {
            resolveRefreshSubscribers(undefined)
            return undefined
        }

        if (response.status === 403) {
            resolveRefreshSubscribers(undefined)
            if (window.location.pathname !== '/auth') {
                window.location.href = '/auth'
            }
            return undefined
        }

        resolveRefreshSubscribers(undefined)
        return undefined
    } catch {
        resolveRefreshSubscribers(undefined)
        return undefined
    } finally {
        isRefreshingAccess = false
    }
}

const api = async <dataType = any>(
    object: string | undefined,
    command: string,
    data: any = undefined,
    config?: {
        signal?: AbortSignal
    }
) => {
    if (!object) throw new Error('Отсутствует объект запроса')

    const {data: responseData, headers} = await instanse.post<TApiResponse<dataType>>(
        `?q=${object}__${command}`,
        {object, command, data},
        {signal: config?.signal}
    )

    const ctype = (headers['content-type'] || '').toLowerCase()
    if (ctype.includes('text') || ctype.includes('excel')) return responseData

    if (responseData.status !== 200) {
        throw new Error(
            typeof responseData.data === 'string' ? responseData.data : 'Что-то пошло не так'
        )
    }
    /*
    Для оплаты через ТКасса, ловим по команде visits/add и paymentUrl в ответе
     */
    try {
        const paymentUrl = (responseData as any)?.data?.paymentUrl as string | undefined
        const isPaymentInitRoute = object === 'visits' && command === 'add'

        if (paymentUrl && isPaymentInitRoute) {
            window.location.href = paymentUrl
            return responseData
        }
    } catch {}

    if (object === 'profile' && command === 'update') {
        await Promise.resolve(onProfileUpdated?.())
    }

    return responseData
}

const fileInstance = axios.create({
    headers: {
        'Content-Type': 'blob',
    },
    responseType: 'arraybuffer',
    withCredentials: false,
})

fileInstance.interceptors.request.use(async (config) => {
    const base = await getApiUrl()
    if (!base) return Promise.reject(new Error('API URL не определён'))
    config.baseURL = base

    config.withCredentials = withCredentialsForApiRequests()

    if (!config.headers) config.headers = {}

    if (!useTokens()) {
        const t = getAccessToken()
        if (t && getAuthMode() !== 'token') setAuthMode('token')
    }

    if (useTokens()) {
        const token = getAccessToken()
        if (token) config.headers['Authorization'] = `Bearer ${token}`
    } else {
        delete (config.headers as any)['Authorization']
    }

    return config
})

fileInstance.interceptors.response.use(
    async (response) => {
        const original = response.config as AxiosRequestConfig & {_retry?: boolean}

        const ctype = String(
            (response.headers as any)?.['content-type'] ||
                (response.headers as any)?.['Content-Type'] ||
                ''
        ).toLowerCase()

        if (
            ctype.includes('application/json') ||
            ctype.includes('+json') ||
            ctype.endsWith('/json')
        ) {
            let json: any = null
            try {
                const text = new TextDecoder('utf-8').decode(response.data as ArrayBuffer)
                json = JSON.parse(text)
            } catch {
                return response
            }

            if (json && typeof json.status === 'number') {
                if (json.status === 301 || json.status === 302) {
                    const err = new Error(
                        typeof json.data === 'string'
                            ? json.data
                            : 'Требуется повторная авторизация'
                    )
                    ;(err as any).__APP_NEED_RELOGIN__ = true
                    throw err
                }

                if ((json.status === 401 || json.status === 403) && !onAuthRoute()) {
                    if (!original._retry) {
                        original._retry = true
                        const newToken = await refreshAccessToken()
                        if (newToken) return fileInstance(original)
                    }
                    const err = new Error(
                        typeof json.data === 'string' ? json.data : 'Не авторизован'
                    )
                    throw err
                }

                if (json.status !== 200) {
                    const err = new Error(
                        typeof json.data === 'string' ? json.data : `Ошибка (${json.status})`
                    )
                    ;(err as any).__APP_STATUS__ = json.status
                    throw err
                }

                return {...response, data: json}
            }

            return response
        }

        return response
    },
    async (error: AxiosError) => {
        const original = error.config as AxiosRequestConfig & {_retry?: boolean}
        const status = error.response?.status

        if ((status === 401 || status === 403) && !original?._retry && !onAuthRoute()) {
            original._retry = true
            const newAccess = await refreshAccessToken()
            if (newAccess) return fileInstance(original)
        }
        return Promise.reject(error)
    }
)

export const fileApi = async <dataType = any>(
    object: string | undefined,
    command: string,
    data: any = undefined
) => {
    if (!object) {
        throw new Error('Отсутствует объект запроса')
    }

    const {data: responseData, headers} = await fileInstance.post<TApiResponse<dataType>>(
        `?q=${object}__${command}`,
        {object, command, data}
    )

    const ctype = String(
        (headers as any)['content-type'] || (headers as any)['Content-Type'] || ''
    ).toLowerCase()

    if (ctype.includes('application/json') || ctype.includes('+json') || ctype.endsWith('/json')) {
        return responseData
    }

    if (ctype.includes('text') || ctype.includes('excel')) {
        return responseData
    }

    return responseData
}

export const uploadFiles = async (object: string, command: string, data: any) => {
    const formData = new FormData()
    formData.append('object', object)
    formData.append('command', command)

    const updatedId = data?.id ?? data?.data?.id

    const shouldNotifyProfileUpdated =
        (object === 'profile' && command === 'update') ||
        (object === 'users' &&
            command === 'update' &&
            updatedId &&
            currentUserId &&
            Number(updatedId) === Number(currentUserId))

    const isFileLike = (v: any) =>
        v instanceof File ||
        v instanceof Blob ||
        (v && typeof v === 'object' && v.type && typeof v.arrayBuffer === 'function')

    const extractValue = (v: any) => (v && typeof v === 'object' && 'value' in v ? v.value : v)

    for (const key in data) {
        const value = data[key]

        if (Array.isArray(value)) {
            const hasFiles = value.some(isFileLike)

            if (hasFiles) {
                // массив файлов: оставляем старую схему _key__i
                value.forEach((file, index) => {
                    formData.append(`_${key}__${index}`, file)
                })
            } else {
                value.forEach((item) => {
                    formData.append(`${key}[]`, extractValue(item))
                })
            }
        } else {
            if (isFileLike(value)) {
                formData.append(`_${key}__0`, value)
            } else {
                formData.append(key, extractValue(value))
            }
        }
    }

    const {data: responseData} = await instanse.post<TApiResponse<any>>(
        `?q=${object}__${command}`,
        formData,
        {headers: {'Content-Type': 'multipart/form-data'}}
    )

    if (responseData.status !== 200) {
        throw new Error(
            typeof responseData.data === 'string' ? responseData.data : 'Что-то пошло не так'
        )
    }

    if (shouldNotifyProfileUpdated) {
        await Promise.resolve(onProfileUpdated?.())
    }

    return responseData
}

export const stringifyRequest = async (request: string) => {
    try {
        const stringAsJSON = JSON.parse(request)
        const {data: responseData, headers} = await instanse.post(
            `?q=toolbarRequest`,
            Object.assign(stringAsJSON)
        )
        //выгрузка простых документов (CSV, text)
        if (headers['content-type'].includes('text')) {
            return responseData
        } else {
            if (responseData.status !== 200) {
                throw new Error(
                    typeof responseData.data === 'string'
                        ? responseData.data
                        : 'Что-то пошло не так'
                )
            } else {
                return responseData
            }
        }
    } catch (error) {
        //@ts-ignore
        getErrorToast(error.message)
    }
}

type TMessengerApi = {
    endpoint: string
    data?: {[key: string]: any}
    config?: {signal?: AbortSignal}
}

export const messengerApi = async <dataType = any>(props: TMessengerApi) => {
    const {endpoint, data = {}, config = {}} = props
    if (!endpoint) throw new Error('Отсутствует конечный адрес запроса')

    const token = getAccessToken()
    const headers: Record<string, string> = {'Content-Type': 'application/json'}
    if (token) headers['Authorization'] = `Bearer ${token}`

    const {data: responseData, headers: respHeaders} = await axios.post<TApiResponse<dataType>>(
        `https://bot.docacrm.com${endpoint}`,
        data,
        {headers, signal: config?.signal}
    )

    const ctype = (respHeaders['content-type'] || '').toLowerCase()
    if (ctype.includes('text') || ctype.includes('excel')) {
        return responseData
    } else {
        if (Number(responseData.status) !== 200) {
            throw new Error(
                typeof responseData.data === 'string' ? responseData.data : 'Что-то пошло не так'
            )
        } else {
            return responseData
        }
    }
}

try {
    // @ts-ignore
    if (!(window as any).__TKASSA_FINALIZE_INIT__) {
        // @ts-ignore
        ;(window as any).__TKASSA_FINALIZE_INIT__ = true

        initPaymentsFinalize(api as any)
    }
} catch (e) {}

export default api
